import React, { useState } from 'react';
import { HashRouter, Routes, Route, Link } from 'react-router-dom';
import { ListTodo, Package, Menu, ShoppingCart, Download, Wifi, WifiOff, Info, FileUp, LogOut, Settings } from 'lucide-react';
import { OrderList } from './components/OrderList';
import { Dashboard } from './components/Dashboard';
import { LoginPage } from './components/LoginPage';
import { PrivateRoute } from './components/PrivateRoute';
import { MobileMenu } from './components/MobileMenu';
import { WooProducts } from './components/WooProducts';
import { BackupPage } from './components/BackupPage';
import { DatabaseViewer } from './components/DatabaseViewer';
import { CsvImporter } from './components/CsvImporter';
import { ConnectionPage } from './components/ConnectionPage';
import { MobileLogs } from './components/MobileLogs';
import { useAppContext } from './lib/AppContext';

function App() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isDemoMode, enableDemoMode, disableDemoMode, isOnline } = useAppContext();
  const connectionName = localStorage.getItem('connection_name');

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    window.location.href = '/login';
  };

  return (
    <HashRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/mobile" element={<MobileLogs />} />
        <Route
          path="*"
          element={
            <PrivateRoute>
              <div className="min-h-screen bg-gray-50">
                <nav className="bg-white shadow-sm">
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between h-16">
                      <div className="flex">
                        <div className="flex-shrink-0 flex items-center">
                          <h1 className={`text-xl font-bold ${isDemoMode ? 'text-orange-600' : 'text-green-600'}`}>
                            Lotis Mode {connectionName && <span>({connectionName})</span>}
                          </h1>
                        </div>
                        <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                          <Link
                            to="/"
                            className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-900"
                          >
                            <Package className="w-4 h-4 mr-2" />
                            Live
                          </Link>
                          <Link
                            to="/orders"
                            className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 hover:text-gray-900"
                          >
                            <ListTodo className="w-4 h-4 mr-2" />
                            Commande
                          </Link>
                          <Link
                            to="/woo"
                            className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 hover:text-gray-900"
                          >
                            <ShoppingCart className="w-4 h-4 mr-2" />
                            Woo
                          </Link>
                          <Link
                            to="/sauvegarde"
                            className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 hover:text-gray-900"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Sauvegarde
                          </Link>
                          <Link
                            to="/database"
                            className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 hover:text-gray-900"
                          >
                            <Info className="w-4 h-4 mr-2" />
                            Infos
                          </Link>
                          <Link
                            to="/import-csv"
                            className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 hover:text-gray-900"
                          >
                            <FileUp className="w-4 h-4 mr-2" />
                            Import
                          </Link>
                          <Link
                            to="/connection"
                            className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 hover:text-gray-900"
                          >
                            <Settings className="w-4 h-4 mr-2" />
                            Connection
                          </Link>
                        </div>
                      </div>
                      <div className="flex items-center">
                        {/* Indicateur de connexion - uniquement quand hors ligne */}
                        <div className="mr-4 flex items-center">
                          {!isOnline && (
                            <div className="flex items-center text-orange-600">
                              <WifiOff className="h-4 w-4 mr-1" />
                              <span className="text-xs">Vous êtes hors ligne</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Bouton mode hors-ligne */}
                        {isDemoMode ? (
                          <button
                            onClick={disableDemoMode}
                            className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded mr-4"
                          >
                            <Wifi className="h-4 w-4" />
                          </button>
                        ) : (
                          <button
                            onClick={enableDemoMode}
                            className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded mr-4"
                          >
                            <WifiOff className="h-4 w-4" />
                          </button>
                        )}
                        
                        <button
                          onClick={handleLogout}
                          className="text-gray-500 hover:text-gray-700 text-sm font-medium mr-4"
                        >
                          <LogOut className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setIsMobileMenuOpen(true)}
                          className="sm:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
                        >
                          <Menu className="h-6 w-6" />
                        </button>
                      </div>
                    </div>
                  </div>
                </nav>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/orders" element={<OrderList />} />
                    <Route path="/woo" element={<WooProducts />} />
                    <Route path="/sauvegarde" element={<BackupPage />} />
                    <Route path="/database" element={<DatabaseViewer />} />
                    <Route path="/import-csv" element={<CsvImporter />} />
                    <Route path="/connection" element={<ConnectionPage />} />
                  </Routes>
                </div>

                <MobileMenu
                  isOpen={isMobileMenuOpen}
                  onClose={() => setIsMobileMenuOpen(false)}
                />
              </div>
            </PrivateRoute>
          }
        />
      </Routes>
    </HashRouter>
  );
}

export default App;